import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NgTemplateOutlet, NgStyle } from '@angular/common';

import { SsrService } from '@services/ssr.service';
import { ScrollbarService } from '@services/scrollbar.service';

@Component({
  selector: 'app-scrollbar',
  imports: [NgTemplateOutlet, NgStyle],
  templateUrl: './scrollbar.component.html',
  styleUrls: ['./scrollbar.component.scss'],
})
export class ScrollbarComponent implements AfterViewInit, OnChanges, OnDestroy {
  private ssrService = inject(SsrService);
  private scrollbarService = inject(ScrollbarService);
  @Input() id: string = 'scrollbar';
  @Input() width?: number;
  @Input() height?: string;
  @Input() scroll?: number;
  @Input() active: boolean = true;
  @Input('padding-y') paddingY: number = 0;
  @Input('padding-x') paddingX: number = 0;
  @Input('scrollbar-y') scrollbarY: number = 0;
  @Input('scrollbar-x') scrollbarX: number = 0;
  @Input() gap: number = 0;
  @Input() disabled: boolean = false;
  @Output() scrollChange: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('scrollbar') scrollbar?: ElementRef;
  @ViewChild('scrollbarMain') scrollbarMain!: ElementRef;
  public anchorHeight?: number;
  public anchorPosition?: number;
  public moving: boolean = false;
  public resizeObserver?: ResizeObserver;
  private clickOffsetY: number = 0;

  @HostListener('window:resize', ['$event']) onResize(): void {
    this.init();
  }

  @HostListener('window:mouseup') mouseUp(): void {
    if (this.moving) this.moving = false;
  }

  @HostListener('window:mousemove', ['$event']) mouseMove(event: MouseEvent, smooth: boolean = false): void {
    if (!this.moving) return;

    const scrollbar = this.scrollbar?.nativeElement.querySelector('.scrollbar__content');
    const scrollbarScroll = this.scrollbar?.nativeElement.querySelector('.scrollbar__scroll-inner');

    if (scrollbar && scrollbarScroll) {
      let scrollPosition = event.clientY - scrollbarScroll.getBoundingClientRect().top - this.clickOffsetY;
      scrollPosition = (scrollPosition / scrollbarScroll.offsetHeight) * scrollbar.scrollHeight;

      this.scrollChange.emit(scrollPosition);
      scrollbar.scrollTo({
        top: scrollPosition,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }

  mouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.moving = true;
  }

  onScroll(): void {
    this.init();
  }

  click(event: MouseEvent): void {
    const el = event.target as HTMLElement;
    if (el.classList.contains('scrollbar__anchor')) return;
    event.preventDefault();
    this.moving = true;
    this.mouseMove(event, true);
    this.moving = false;
  }

  startDragging(event: MouseEvent): void {
    event.preventDefault();
    this.moving = true;
    const scrollbarAnchor = this.scrollbar?.nativeElement.querySelector('.scrollbar__anchor');
    if (scrollbarAnchor) {
      this.clickOffsetY = event.clientY - scrollbarAnchor.getBoundingClientRect().top;
    }
  }

  init(): void {
    const scrollbar = this.scrollbar?.nativeElement.querySelector('.scrollbar__content');
    if (!scrollbar) return;
    this.anchorHeight = (100 / scrollbar.scrollHeight) * scrollbar.offsetHeight;
    this.anchorPosition = (scrollbar.scrollTop + scrollbar.offsetHeight) / (scrollbar.scrollHeight / 100) - this.anchorHeight;
    this.anchorPosition = Math.max(0, this.anchorPosition);
    this.scrollChange.emit(this.anchorPosition);
    this.scrollbarService.setValue(this.id, this.anchorPosition);
    if (this.scroll) scrollbar.scrollTo(0, this.scroll);
  }

  ngAfterViewInit(): void {
    if (this.ssrService.isServer() || !this.scrollbar) return;
    this.resizeObserver = new ResizeObserver(() => this.init());
    this.resizeObserver.observe(this.scrollbarMain.nativeElement);
  }

  ngOnChanges(change: SimpleChanges): void {
    if (this.ssrService.isServer()) return;
    if (change['height'] && !change['height'].firstChange) {
      const scrollbar = this.scrollbar?.nativeElement.querySelector('.scrollbar__content');
      scrollbar.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  ngOnDestroy(): void {
    if (this.ssrService.isServer()) return;
    this.resizeObserver?.disconnect();
  }
}
